import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { FabricatedDataNotice } from '../components/brand/FabricatedDataNotice';
import { clientsAPI } from '../lib/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Storefront, Lightning, CircleNotch, Warning, ArrowUp, ArrowDown, Package,
  CurrencyDollar, ShoppingCart, Timer, CheckCircle, Pause
} from '@phosphor-icons/react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('nx_token')}` } });

export default function MarketplacePage() {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [overview, setOverview] = useState(null);
  const [listings, setListings] = useState(null);
  const [platform, setPlatform] = useState('amazon_in');
  const [loading, setLoading] = useState(true);
  const [repricing, setRepricing] = useState(false);
  const [repriceResult, setRepriceResult] = useState(null);
  const [stockGuard, setStockGuard] = useState(null);
  const [guardRunning, setGuardRunning] = useState(false);

  useEffect(() => {
    clientsAPI.list().then(r => { setClients(r.data); if (r.data.length > 0) setSelectedClient(r.data[0].id); })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedClient) return;
    axios.get(`${API}/marketplace/overview/${selectedClient}`, auth()).then(r => setOverview(r.data)).catch(() => {});
    axios.get(`${API}/marketplace/listings/${selectedClient}?platform=${platform}`, auth()).then(r => setListings(r.data)).catch(() => {});
  }, [selectedClient, platform]);

  const handleReprice = async () => {
    setRepricing(true);
    try {
      const res = await axios.post(`${API}/marketplace/reprice/${selectedClient}?platform=${platform}`, {}, auth());
      setRepriceResult(res.data);
      toast.success(`${res.data.total_products_repriced} repricing suggestions`);
    } catch { toast.error('Repricing failed'); }
    finally { setRepricing(false); }
  };

  const handleStockGuard = async () => {
    setGuardRunning(true);
    try {
      const res = await axios.post(`${API}/marketplace/stock-guard/${selectedClient}`, {}, auth());
      setStockGuard(res.data);
      toast.success(`${res.data.ads_paused} ads paused, $${res.data.total_spend_saved} saved`);
    } catch { toast.error('Stock guard failed'); }
    finally { setGuardRunning(false); }
  };

  const mpChart = overview ? Object.entries(overview.marketplaces || {}).map(([k, v]) => ({
    name: v.name?.replace('Amazon ', '').substring(0, 10), revenue: v.revenue_30d, buyBox: v.buy_box_win_rate,
  })) : [];

  if (loading) return <DashboardLayout><div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[var(--accent-border)] border-t-[var(--accent)] rounded-full animate-spin" /></div></DashboardLayout>;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="marketplace-page">
        <FabricatedDataNotice reason="Every marketplace, repricing, stock-guard, and Q-Commerce number below is randomly generated on each request (backend returns source: 'mock') — there is no real Amazon, Flipkart, Meesho, Noon, Blinkit, Zepto, or Swiggy Instamart account connected in this environment, so revenue, buy-box, stock, and order figures are illustrative, not a measurement of real marketplace performance." />
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-[var(--text-primary)] tracking-tight">
              Marketplace & Q-Commerce
            </h1>
            <p className="text-sm text-[var(--text-secondary)] mt-1">Amazon, Flipkart, Meesho, Noon + Blinkit, Zepto, Swiggy Instamart</p>
          </div>
          <Select value={selectedClient} onValueChange={setSelectedClient}>
            <SelectTrigger className="w-48 bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)]"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[var(--surface)] border-[var(--border)]">
              {clients.map(c => <SelectItem key={c.id} value={c.id} className="text-[var(--text-primary)] hover:bg-[var(--neutral-bg)]">{c.display_name || c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {overview && (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: 'Total Revenue', value: `$${(overview.total_revenue_30d || 0).toLocaleString()}`, icon: CurrencyDollar, color: 'var(--success)' },
                { label: 'Avg Buy-Box', value: `${overview.avg_buy_box}%`, icon: ShoppingCart, color: 'var(--accent)' },
                { label: 'Stock Alerts', value: overview.total_stock_alerts, icon: Warning, color: overview.total_stock_alerts > 5 ? 'var(--error)' : 'var(--warning)' },
                { label: 'Platforms', value: Object.keys(overview.marketplaces || {}).length + Object.keys(overview.qcommerce || {}).length, icon: Storefront, color: 'var(--accent)' },
              ].map(m => {
                const Icon = m.icon;
                return (
                  <div key={m.label} className="nx-metric-card rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon size={16} style={{ color: m.color }} weight="duotone" />
                      <span className="text-[10px] text-[var(--text-tertiary)] font-mono uppercase tracking-wider">{m.label}</span>
                    </div>
                    <p className="text-2xl font-bold font-mono" style={{ color: m.color }}>{m.value}</p>
                  </div>
                );
              })}
            </div>

            <Tabs defaultValue="listings" className="space-y-4">
              <TabsList className="bg-[var(--surface)] border border-[var(--border)]">
                <TabsTrigger value="listings" className="data-[state=active]:bg-[var(--accent)] data-[state=active]:text-white">Listings</TabsTrigger>
                <TabsTrigger value="reprice" className="data-[state=active]:bg-[var(--accent)] data-[state=active]:text-white">Repricing</TabsTrigger>
                <TabsTrigger value="stockguard" className="data-[state=active]:bg-[var(--accent)] data-[state=active]:text-white">Stock Guard</TabsTrigger>
                <TabsTrigger value="qcommerce" className="data-[state=active]:bg-[var(--accent)] data-[state=active]:text-white">Q-Commerce</TabsTrigger>
              </TabsList>

              {/* Listings */}
              <TabsContent value="listings" className="space-y-4">
                <div className="flex items-center gap-3">
                  <Select value={platform} onValueChange={setPlatform}>
                    <SelectTrigger className="w-44 bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)]"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[var(--surface)] border-[var(--border)]">
                      {Object.entries(overview.marketplaces || {}).map(([k, v]) => (
                        <SelectItem key={k} value={k} className="text-[var(--text-primary)] hover:bg-[var(--neutral-bg)]">{v.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {listings && (
                  <div className="nx-metric-card rounded-lg">
                    <table className="nx-table">
                      <thead><tr><th>Product</th><th>SKU</th><th>Price</th><th>Buy Box</th><th>Stock</th><th>Orders</th><th>Rating</th><th>Health</th></tr></thead>
                      <tbody>
                        {listings.listings?.map(l => (
                          <tr key={l.id}>
                            <td className="!text-[var(--text-primary)] text-xs">{l.product_name}</td>
                            <td className="font-mono text-xs text-[var(--accent)]">{l.asin_sku}</td>
                            <td className="font-mono">${l.price}</td>
                            <td>{l.buy_box_winner ? <CheckCircle size={14} className="text-[var(--success)]" weight="fill" /> : <span className="text-[var(--error)] text-xs">Lost</span>}</td>
                            <td className={`font-mono ${l.stock_alert ? 'text-[var(--error)] font-bold' : ''}`}>{l.stock}{l.stock_alert && ' !'}</td>
                            <td className="font-mono">{l.orders_30d}</td>
                            <td className="font-mono">{l.rating}</td>
                            <td><span className={`font-mono text-xs ${l.health_score >= 70 ? 'text-[var(--success)]' : 'text-[var(--warning)]'}`}>{l.health_score}%</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </TabsContent>

              {/* Repricing */}
              <TabsContent value="reprice" className="space-y-4">
                <Button onClick={handleReprice} disabled={repricing} className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white">
                  {repricing ? <CircleNotch size={16} className="animate-spin mr-2" /> : <Lightning size={16} className="mr-2" />}
                  Run Intelligent Repricing
                </Button>
                {repriceResult && (
                  <div className="nx-metric-card rounded-lg">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-display text-sm font-bold text-[var(--text-primary)]">Repricing Suggestions</h3>
                      <span className="nx-tag nx-tag-green">{repriceResult.expected_overall_lift}</span>
                    </div>
                    <table className="nx-table">
                      <thead><tr><th>Product</th><th>Current</th><th>Suggested</th><th>Buy Box</th><th>Expected Lift</th><th>Margin Impact</th></tr></thead>
                      <tbody>
                        {repriceResult.repricing_suggestions?.map((r, i) => (
                          <tr key={i}>
                            <td className="!text-[var(--text-primary)] text-xs">{r.product}</td>
                            <td className="font-mono">${r.current_price}</td>
                            <td className="font-mono text-[var(--success)] font-bold">${r.suggested_price}</td>
                            <td className="font-mono text-[var(--text-tertiary)]">${r.buy_box_price}</td>
                            <td className="font-mono text-[var(--success)]">{r.expected_buy_box_lift}</td>
                            <td className="font-mono text-[var(--warning)]">{r.margin_impact}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </TabsContent>

              {/* Stock Guard */}
              <TabsContent value="stockguard" className="space-y-4">
                <Button onClick={handleStockGuard} disabled={guardRunning} className="bg-[var(--error)] hover:opacity-90 text-white">
                  {guardRunning ? <CircleNotch size={16} className="animate-spin mr-2" /> : <Pause size={16} className="mr-2" />}
                  Run Stock-Out Prevention
                </Button>
                {stockGuard && (
                  <div className="nx-metric-card rounded-lg">
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="p-3 rounded border border-[var(--border-light)] text-center">
                        <p className="text-2xl font-bold text-[var(--error)] font-mono">{stockGuard.ads_paused}</p>
                        <p className="text-xs text-[var(--text-tertiary)]">Ads Paused</p>
                      </div>
                      <div className="p-3 rounded border border-[var(--border-light)] text-center">
                        <p className="text-2xl font-bold text-[var(--success)] font-mono">${stockGuard.total_spend_saved}</p>
                        <p className="text-xs text-[var(--text-tertiary)]">Spend Saved</p>
                      </div>
                    </div>
                    {stockGuard.actions?.map((a, i) => (
                      <div key={i} className="flex items-start gap-3 p-3 rounded border border-[var(--error-border)] mb-2">
                        <Warning size={16} className="text-[var(--error)] mt-0.5 flex-shrink-0" weight="fill" />
                        <div className="flex-1">
                          <p className="text-xs text-[var(--text-primary)] font-medium">{a.product} ({a.sku})</p>
                          <p className="text-[10px] text-[var(--text-secondary)]">{a.reason}</p>
                        </div>
                        <span className="font-mono text-xs text-[var(--success)]">-${a.ad_spend_saved}</span>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Q-Commerce */}
              <TabsContent value="qcommerce" className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {Object.entries(overview.qcommerce || {}).map(([k, q]) => (
                    <div key={k} className="nx-metric-card rounded-lg">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-display text-sm text-[var(--text-primary)] font-bold">{q.name}</h4>
                        <span className={`nx-tag ${q.in_stock_rate >= 90 ? 'nx-tag-green' : 'nx-tag-yellow'}`}>{q.in_stock_rate}% in-stock</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div><p className="text-xs text-[var(--text-tertiary)]">Active SKUs</p><p className="text-lg font-bold text-[var(--text-primary)] font-mono">{q.active_skus}</p></div>
                        <div><p className="text-xs text-[var(--text-tertiary)]">Orders Today</p><p className="text-lg font-bold text-[var(--accent)] font-mono">{q.orders_today}</p></div>
                        <div><p className="text-xs text-[var(--text-tertiary)]">Avg Delivery</p><p className="text-lg font-bold text-[var(--text-primary)] font-mono">{q.avg_delivery_min}m</p></div>
                        <div><p className="text-xs text-[var(--text-tertiary)]">Low Stock</p><p className={`text-lg font-bold font-mono ${q.low_stock_alerts > 0 ? 'text-[var(--error)]' : 'text-[var(--success)]'}`}>{q.low_stock_alerts}</p></div>
                      </div>
                    </div>
                  ))}
                </div>
              </TabsContent>
            </Tabs>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
